import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button, Select } from '@/components/ui';
import { COLLECTION_MALLS, type CollectionMall } from '../productCollection/collectionMalls';
import type { ProductCollectionConfig } from '../productCollection/types';
import type { ProductCollectionByMall } from '../productCollection/ProductCollectionMallTabs';
import { appendTemplate, builtInTemplates, countAdded } from './templateModel';
import { useCollectionTemplatesQuery } from './useCollectionTemplates';

/**
 * QA-563 [템플릿 불러오기] — 고른 템플릿의 4몰 항목을 상품의 몰별 추가입력 뒤에 한 번에 붙인다(같은 라벨은 건너뜀).
 * 템플릿 이름은 관리자용이라 상품에는 항목만 복사된다. 붙인 뒤에도 행 추가·삭제·수정은 평소처럼.
 */
export const CollectionTemplateApplyBar = ({
  value,
  disabled,
  onChange,
}: {
  value: ProductCollectionByMall;
  disabled?: boolean;
  onChange: (mall: CollectionMall, next: ProductCollectionConfig) => void;
}) => {
  const saved = useCollectionTemplatesQuery();
  const templates = useMemo(() => [...builtInTemplates(), ...(saved.data ?? [])], [saved.data]);
  const [templateId, setTemplateId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const picked = templates.find((t) => t.templateId === templateId) ?? null;

  const apply = () => {
    if (!picked) return;
    const added = countAdded(value, picked.malls);
    if (added === 0) {
      setNotice('이미 같은 항목이 모두 있어 더할 항목이 없습니다.');
      return;
    }
    if (!window.confirm(`"${picked.name}" 템플릿의 항목 ${added}개를 4개 몰(국내·일문·중문·영문)에 더합니다. 같은 이름의 항목은 건너뜁니다.`)) return;
    for (const mall of COLLECTION_MALLS) onChange(mall, appendTemplate(value[mall], picked.malls[mall]));
    setNotice(`"${picked.name}" 항목을 더했습니다. 몰별 탭에서 행을 추가·삭제할 수 있습니다.`);
  };

  return (
    <div className="mb-4 flex flex-col gap-1.5 rounded-md p-3" style={{ background: 'var(--surface-1)' }} data-testid="collection-template-apply">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-[color:var(--content-strong)]">템플릿 불러오기</span>
        <Select
          className="w-64"
          value={templateId}
          onChange={(e) => {
            setTemplateId(e.target.value);
            setNotice(null);
          }}
          disabled={disabled}
          aria-label="추가입력 템플릿"
          data-testid="collection-template-select"
        >
          <option value="">템플릿 선택</option>
          {templates.map((t) => (
            <option key={t.templateId} value={t.templateId}>
              {t.name}
            </option>
          ))}
        </Select>
        <Button type="button" size="sm" variant="outline" disabled={disabled || !picked} onClick={apply} data-testid="collection-template-apply-button">
          4개 몰에 적용
        </Button>
        <Link to="/products/collection-templates" className="ml-auto text-xs underline text-[color:var(--content-muted)]">
          템플릿 관리
        </Link>
      </div>
      <p className="text-[11px] text-[color:var(--content-muted)]">
        템플릿 이름은 고객에게 보이지 않고, 항목(라벨·입력 방식)만 상품에 복사됩니다. 템플릿을 나중에 고쳐도 이미 적용한 상품은 바뀌지 않습니다.
      </p>
      {notice ? (
        <p role="status" className="text-[12px]" style={{ color: 'var(--status-info-fg)' }} data-testid="collection-template-notice">
          {notice}
        </p>
      ) : null}
    </div>
  );
};
